/**
 * use-live-preview-refresh — keeps the pinned rotation preview HONEST. The
 * panel renders a mutation snapshot, so a queue change underneath it (move to
 * queue/waiting, priority drag, bring back…) used to leave a stale plan — the
 * new "replaces X" only appeared after closing and reopening the drawer. Now:
 * whenever the queue query delivers data NEWER than the shown preview, the
 * preview silently re-runs. One attempt per queue update (an erroring preview
 * must not retry-loop); nothing runs while no preview is shown.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef } from 'react';
import type { QueueListResult } from '@trafficpulse/shared';

interface PreviewLike {
  data: unknown;
  isPending: boolean;
  mutate: () => void;
}

export function useLivePreviewRefresh(
  queueUpdatedAt: number,
  preview: PreviewLike,
): void {
  const resultAt = useRef(0);
  const attemptedFor = useRef(0);

  // Stamp when a preview result lands so "newer queue data" is detectable.
  useEffect(() => {
    if (preview.data) resultAt.current = Date.now();
  }, [preview.data]);

  useEffect(() => {
    if (!preview.data || preview.isPending) return;
    if (queueUpdatedAt <= resultAt.current) return;
    if (attemptedFor.current === queueUpdatedAt) return;
    attemptedFor.current = queueUpdatedAt;
    preview.mutate();
    // `preview.mutate` is intentionally NOT a dep — its identity changes per
    // render and re-running on it would loop; the attempt gate keys the run.
  }, [queueUpdatedAt, preview.data, preview.isPending]);
}

/**
 * Which elements are LIVE on the flow right now. The weights preview is computed
 * server-side from the live flow, so this is exactly the input that makes it go
 * stale — and nothing else. Keying on it (rather than the queue query's
 * `dataUpdatedAt`) means a routine 15s poll or a window refocus costs nothing.
 */
export function liveFlowSignature(data: QueueListResult | undefined): string {
  if (!data) return '';
  return (data.testing ?? [])
    .map((e) => `${e.layer}:${e.itemId}`)
    .sort()
    .join('|');
}

/**
 * Keep the PINNED WEIGHTS preview honest across queue actions. That preview
 * refetches on config-draft edits only, but promoting/eliminating an element
 * changes the LIVE FLOW instead — so the panel kept showing the old paths until
 * the drawer was closed and reopened. Now a composition change refetches it.
 *
 * The first signature is recorded WITHOUT refetching: at mount the preview and
 * the queue describe the same server state, so refetching there would just
 * duplicate the request the provider already makes.
 */
export function useLiveFlowPreviewSync(
  signature: string,
  refetch: (() => void) | undefined,
): void {
  const seen = useRef<string | null>(null);

  useEffect(() => {
    if (!signature || !refetch) return;
    if (seen.current === null || seen.current === signature) {
      seen.current = signature;
      return;
    }
    seen.current = signature;
    refetch();
  }, [signature, refetch]);
}
